/**
 * @file        identity.js - library for acquiring and working with DCP identities. This library
 *              has extra exports added to it by operation-descriptions.js when loaded in a browser.
 *              Practical note: the majority of the time, this code works with proxied identities.
 *
 *              This library stores and manages one or more identity contexts and identity sessions.
 *
 *              An identity context represents the data stored in this browser (or .dcp directory)
 *              for a single Distributive Identity. Metadata about the identity, such as the public
 *              address, user's name, avatar link, etc.
 *
 *              A identity session represents the sensitive data stored in this browser (or .dcp
 *              directory) for a single Distributive Identity. In particular, we store an identity
 *              keystore and a session id.
 *
 *              Identity contexts are stored in local storage on a d9ve domain
 *              Identity sessions are stored in session storage on a third-party domain
 *                -> auth subsystem uses a $login-priviledged session so that it can access the portal
 *                   wallet for <portal-wallet> tags, create reduced-privs keys with new-px, etc.
 *
 *              Each context is identified by the public address of the de-proxied identity.
 *              Each session is identified by a session id. The id is generated during the authorization
 *              phase by the app requesting authorization, and is fetched from the session by embedding
 *              an iFrame in the app which has access to the session storage, and using the session id
 *              to send it back by post message.
 *
 *              Flows:
 *              ---------------------------------------------------------
 *              new user -> login -> sign up -> have-context
 *              new browser -> login -> have-context
 *              have-contexts -> pick-context -> have-context
 *              have-context -> authenticate -> authorize -> have-session
 *
 *              - authenticate means to get an identity with pxAuth.new permission
 *              - authorize means to use this identity to create an identity with
 *                only the permissions needed by the app, under the supervision of
 *                the user on our secure domain
 *
 * @author      Wes Garland
 * @date        June 2024
 */
'use strict';
const identity = exports;
const wallet   = require('dcp/dcp-client/wallet');

const { DCPError }            = require('dcp/common/dcp-error');
const { Keystore,
        IdKeystore,
        PrivateKey,
        Address }             = wallet;
const { submitSignedPost }    = require('dcp/utils');
const { isBrowserPlatform,
        platform }            = require('dcp/common/dcp-env');
const { assert }              = require('dcp/common/dcp-assert');
const { BigNumber }           = require('dcp/types');
const { randStr21 }           = require('dcp/protocol/utils');
const { localStorage,
        sessionStorage,
      }                       = require('./ls-storage.js');
const { requireNative }       = require('dcp/dcp-client/webpack-native-bridge');

var activeSession = false; /* Authoritative singleton: currently-active session */
const debugging = require('dcp/debugging').scope('identity');
const s$sdMemo = Symbol('setDefault memo');

/* Make module an event emitter */
const _ee = new (require('events').EventEmitter)();
for (let prop in _ee.constructor.prototype)
  if (typeof _ee[prop] === 'function')
    exports[prop] = _ee[prop].bind(_ee);

/* Define KVIN context for use by identity subsystem, eg. post messages, session snapshots. */
const KVIN = new (require('kvin').KVIN);
KVIN.userCtors[Address.name]  = Address;
KVIN.userCtors[Keystore.name] = Keystore;
KVIN.userCtors[Keystore.name] = IdKeystore;
exports.KVIN = KVIN;

/* Create a promise that resolves when the DOM is ready */
const p$domContentLoaded = isBrowserPlatform && new Promise((resolve, reject) => {
  if (document.readyState === 'complete') /* loaded dcp-client asynchronously?! */
    setTimeout(resolve, 0);
  else
    window.addEventListener('DOMContentLoaded', resolve);
});

function navigateTo(href)
{
  window.location.assign(href);
}

/**
 * Run a function that that needs document.body. The function is run synchronously if the document body
 * already exists, otherwise we defer the function until the window<load> event fires.
 */
function withBody(fun)
{
  assert(typeof fun === 'function');
  if (document.body)
    fun();
  else
    window.addEventListener('load', fun);
}

/**
 * Get the current identity. It is an error to try to get the identity before login.
 * @returns {IdKeystore}
 */
exports.get = function identity$$get()
{
  if (!activeSession)
    throw new Error('cannot get identity with no session');

  return activeSession.identity;
}

/**
 * Returns the active session object.  User-added properties are be persisted throughout the session
 * across pages automatically when DistributiveSesssion::save() is called.
 */
exports.getActiveSession = function identity$$getActiveSession()
{
  return activeSession;
}

exports.waitForSession = function identity$$waitForSession()
{
  if (activeSession)
    return Promise.resolve();

  return new Promise((resolve, reject) => {
    identity.on('session', resolve);
  });
}

/**
 * Check for an identity in the active session.
 *
 * @returns true when we have a session with an identity
 */
exports.check = function identity$$check()
{
  return Boolean(activeSession?.identity);
}

/**
 * Set the identity. Setting the identity means:
 * 1 - add the identity to the context if not there
 * 2 - use that identity to establish a session
 *
 * @param {any} newIdentity    any valid 1st argument to the Keystore constructor when the second
 *                             argument is the passphrase.
 */
exports.set = async function identity$$set(newIdentity, options)
{
  const { DistributiveSession } = require('./distributive-session');
  debugging() && console.debug('set identity', newIdentity, options);
  if (activeSession?.identity)
    throw new DCPError('identity has already been set', 'EHAVEIDENTITY');

  if (!options)
    options = {};
  if (newIdentity instanceof Promise)
    newIdentity = await newIdentity;
  if (newIdentity instanceof Keystore)
  {
    if (options.passphrase)
      newIdentity.unlock(options.passphrase);
    newIdentity = await newIdentity.getPrivateKey(newIdentity);
  }

  try
  {
    const idKs = await new IdKeystore(newIdentity, ''); /* DCP-4423 */
    const session = activeSession || new DistributiveSession();
    session.identity = idKs;
    if (!activeSession)
      exports.activateSession(session); /* emit identity<set> */
    if (isBrowserPlatform)
      withBody(() => document.body.setAttribute('dcp-identity', session.identity.address));
  }
  catch(error)
  {
    identity.logout();
    throw error;
  }
}

/**
 * Start browser-based session login for dcp-client. This API is called as `identity.login()` by third
 * party applications. This library is used by the pxAuth web pages, but not this function.
 * - first try to get session parent window
 *   - only when window.opener or window.parent
 *   - only when script loaded with parent-login attribute
 *   - send post type='distributive-session-request'
 *   - receive post type='distributive-session-key' that includes session key
 *   - post must come from options.parentAuthOrigin, default same origin
 *
 * - next try to get existing session via localStorage
 *   - existing session id stored in localStorage as distributiveSessionId
 *   - load get-session-key.html from d9ve origin in iframe
 *   - post sessionId to iframe, get session object back
 *
 * - if that doesn't work,
 *   - generate new session id
 *   - visit page hosted on D9ve infra [cross-origin], sending it session id
 *   - d9ve page authenticate.html does oAuth or ksAuth flow
 *   - d9ve page authorize.html creates new px key for session
 *   - redirects back to this page [cross-origin]
 *   - "new" session id is not "existing" session id
 *
 * - Session objects are exchanged over postMessage as KVIN with Address and IdKeystore user ctors.
 *
 * @note Page may reload during this process! You should always await this promise at the earliest place
 *       in your page/program flow. Any state which needs to be preserved should be preserved in session
 *       or local storage and restored when the DOMContentLoaded
 *
 * @returns a Promise which resolves once the identity has been established and the DOMContentLoaded
 *          event has fired.
 *
 * @see normalizePxRequest() for more pxRequest details
 * @example identity.login('$login', 86400);                            // do anything for a day
 * @example identity.login('worker.admin', 3600);                       // administer any worker for an hour
 * @example identity.login({ 'worker.admin': { related: 'abc' }}, 600); // administer worker abc for ten minutes
 */
exports.login_browser = async function identity$$login(pxRequest, options)
{
  const myUrl = new URL(window.location);    /* current page, might have been nextUrl in a previous life */
  const nextUrl = options?.nextUrl || myUrl; /* page that d9ve page redirects to after authorization */
  const { DistributiveSession } = require('./distributive-session');

  if (!pxRequest)
    pxRequest = '$login';
  if (options instanceof Date)
    options = { pxExpireDate: options };
  else if (typeof options === 'number')
    options = { pxExpireSeconds: options };
  if (!options)
    options = {};
  if (!options.sessionOrigin)
    options.sessionOrigin = window.location.origin;
  if (!options.pxExpireDate && !options.pxExpireSeconds)
    options.pxExpireSeconds = 86400;

  /* "Session Recovery" is mostly intended for inter-page navigation, back-button, etc, and is an
   * optimization outside of the various flow scenarios described in the readme.
   */
  if (await identity.recoverSession(pxRequest, options))
    return p$domContentLoaded; /* already logged in -> Sub flow, returning user */

  /* User is returning after authorize? */
  const memoSessionId = false
        || sessionStorage.getItem('dcp-current-session-id')
        || localStorage  .getItem('dcp-current-session-id');
  if (memoSessionId)
  {
    try
    {
      const memo = await DistributiveSession.a$getSessionMemo(memoSessionId);
      if (memo)
      {
        const session = new DistributiveSession(memoSessionId);
        session.restoreFromMemo(memo);
        exports.activateSession(session);
        return p$domContentLoaded;
      }
    }
    catch(error)
    {
      return dcp['dom-tk'].modals.alert(`DCP authorization failure locating session memo (${error.message})`);
    }
  }

  /* Completely new user */
  exports.activateSession(new DistributiveSession());
  return doChooseContextSubFlow();

  /* Sub Flow: Choose Context. This is the first time we have gotten this far, this session. Redirect
   * to the ksAuth / oAuth page so we can authenticate. The next time this page is loaded and
   * identity.login() is called, there should be one available to post to us from get-session-key.html.
   */
  function doChooseContextSubFlow()
  {
    const authUrl = (dcpConfig.oAuth.identityIntegration !== false)
          ? new URL(dcpConfig.oAuth.location.resolve('nyi')) /* stub only, NYI */
          : new URL(dcpConfig.pxAuth.location.resolve('authenticate.html'));
    authUrl.searchParams.set('session', activeSession.id);
    authUrl.searchParams.set('nextUrl', String(nextUrl || window.location));

    const strs = {
      pxRequest:       JSON.stringify(pxRequest),
      pxExpireDate:    JSON.stringify(options.pxExpireDate),
      pxExpireSeconds: JSON.stringify(options.pxExpireSeconds || dcpConfig.pxAuth.defaultExpireSeconds || 600),
    };

    /* If the API user asked for a specific expiry duration in the login options, use that duration to
     * create the the proxy keys in px-new, and pass along the pxRequest from the login() call to px-new
     * (after authorization).
     */
    if (options?.pxExpireDate)
      authUrl.searchParams.set('pxExpireDate', strs.pxExpireDate);
    else
      authUrl.searchParams.set('pxExpireSeconds', strs.pxExpireSeconds);
    authUrl.searchParams.set('pxRequest', strs.pxRequest);

    activeSession.loginLocation = window.location.href;
    activeSession.save();

    navigateTo(authUrl.href);
    assert('unreached');
  }
}

/**
 * Set the default source of the identity for non-browser logins. This can be a private key, filename,
 * or JSON-encoded keystore. The private key mode gives us the option to specify a proxy key that
 * behaves effectively as an API key.
 */
const setDefault = exports.setDefault = function identity$$setDefault(optarg)
{
  setDefault[s$sdMemo] = optarg;
}

/**
 * Non-browser applications need the concept of a default identity because they can't always go through
 * an oAuth-style login flow. The dcp-client library knows how to use identity.setDefault() to reflect
 * the command-line parameter --dcp-identity=.
 *
 * If identity.setDefault() wasn't called, we try to use oAuth or we try to find the identity on disk.
 * If not present, we dynamically create one and save it (unless opertions.persistNewIdentity).
 *
 * @returns {Promise} that resolves to either wallet::PrivateKey or wallet::IdKeystore
 */
async function determineDefaultIdentity(options)
{
  var idKs;
  const { expandPath } = require('dcp/utils');
  const fsBasic = require('dcp/common/fs-basic');
  const ksDir   = require('dcp/utils/resource-dir').getResourceDir();
  const ksFile  = expandPath(options?.keystoreFile) || fsBasic.pathResolve(ksDir, 'id.keystore');

  if (setDefault[s$sdMemo]) /* application program or dcp-client cli opts set this to help us */
  {
    if (wallet.isPrivateKey(setDefault[s$sdMemo]))
      return new wallet.PrivateKey(setDefault[s$sdMemo]);
    else if (setDefault[s$sdMemo][0] === '{')
      return new wallet.IdKeystore(JSON.parse(setDefault[s$sdMemo].trim()));
    else
      return new wallet.IdKeystore(fsBasic.readFile(expandPath(setDefault[s$sdMemo])).trim());
  }

  if (ksFile && fsBasic.fileExists(ksFile))
    idKs = options?.identity || await new IdKeystore(JSON.parse(fsBasic.readFile(ksFile)));
  else
  {
    if (exports.interactive && (dcpConfig.oAuth.identityIntegration !== false))
      throw new Error('identity via oAuth is not supported in this environment');

    const os = require('dcp/common/os-basic');
    idKs = await new IdKeystore(null, '');
    idKs.label = `identity:${os.username()}@${os.hostname()}`;
    if (options?.persistNewIdentity !== false)
    {
      const dir = fsBasic.dirname(ksFile);
      if (!fsBasic.dirExists(dir))
        fsBasic.mkdir(dir);
      fsBasic.writeFileAtomic(ksFile, JSON.stringify(idKs));
    }
  }
  return idKs;
}

/**
 * Start a dcp-client login session in a non-browser environment.
 * Currently limited to $login sessions because those don't require negotiating for a proxy key etc.
 *
 * @returns a Promise which resolves once the identity has been established and the DOMContentLoaded
 *          event has fired.
 */
exports.login_notBrowser = async function identity$$login_notBrowser(pxRequest, options)
{
  const { DistributiveSession } = require('./distributive-session');

  if ((pxRequest && pxRequest !== '$login') || (options?.pxExpireDate || options?.pxExpireSeconds))
    throw new Error('Proxy negotiation currently not supported from this interface');

  const session = new DistributiveSession();
  if (!activeSession?.identity)
    session.identity = await determineDefaultIdentity(options);
  exports.activateSession(session);
}

/**
 * Masquerade - generate an alternate identity based on a "real" identity plus a uniqueString. The
 * resultant session has an identity which is predictable based on the arguments passed, but is not the
 * same PrivateKey (or address) as login would yield. Useful for generating local identities which are
 * pseudo-dynamic. Maybe not so useful in conjunction with proxification requests.
 *
 * @param {string} uniqueString     base identity + unique String yields masquerade identity
 * @param {object} options          Same options as login(), plus baseIdentity
 */
exports.masquerade = async function identity$$masquerade(uniqueString, pxRequest, options)
{
  const { DistributiveSession } = require('./distributive-session');
  const ethUtil = require('../wallet')._internalEth.util;
  const session = new DistributiveSession();

  var baseIdentity = options?.baseIdentity;
  assert(uniqueString);
  if ((pxRequest && pxRequest !== '$login') || (options?.pxExpireDate || options?.pxExpireSeconds))
    throw new Error('Proxy negotiation currently not supported from this interface');

  await exports.logout();
  if (!baseIdentity)
    baseIdentity = await determineDefaultIdentity(options);

  /** @todo clean up when merging wes/fast-crypto */
  const baseIdentityPk = await baseIdentity.getPrivateKey();
  const baseIdentityPkBuf = ethUtil.toBuffer(baseIdentityPk.toString());
  const uniqueStringBuf = Buffer.from(uniqueString);
  const masqueradeHash = ethUtil.keccak256(ethUtil.toBuffer(Buffer.concat([uniqueStringBuf, baseIdentityPkBuf])));
  const masqueradePk = new wallet.PrivateKey(masqueradeHash.toString('hex'));
  session.identity = await new wallet.Keystore(masqueradePk, '');
  exports.activateSession(session);
}

exports.forceLogin = async function identity$$forceLogin(id)
{
  const { DistributiveSession } = require('./distributive-session');
  if (!(id instanceof wallet.Keystore || id instanceof wallet.PrivateKey))
    id = await new wallet.Keystore(id);

  const session = new DistributiveSession();
  if (!activeSession?.identity)
    session.identity = id;
  exports.activateSession(session);
}

/**
 * Logout of the active session. Best-effort is made to remove the proxy keys from the database.
 *
 * @param {string|URL}   postLogoutLocation      string or URL we will navigate to after logout tasks
 *                                               complete.
 * @returns {Promise} which resolves after logout tasks complete.
 */
exports.logout = async function identity$$logout(postLogoutLocation)
{
  identity.clearSession();
  wallet.clear();

  const oldIdentity = activeSession?.identity;
  if (oldIdentity)
  {
    const endpoint = dcpConfig.pxAuth.location.resolve('logout');
    const expiry = Math.floor(Date.now()/1000) + 3600;
    const messageBody = { endpoint, expiry };

    const result = await submitSignedPost(oldIdentity, messageBody, { busy, unbusy });

    if (result instanceof Error)
      throw result;
    debugging() && console.debug('logout:', result);
  }

  if (isBrowserPlatform)
  {
    if (postLogoutLocation)
      window.location.assign(postLogoutLocation);
    withBody(() => document.body.removeAttribute('dcp-identity'));
  }
}

/**
 * Send a message to Althea's new-px endpoint, asking for a new proxy key. This key must have the same
 * or fewer permissions than the identity that is requesting it, or Althea will not create it. This
 * method cannot be used to extend the expiry time of a proxied key.
 *
 * @param {KsCollection} ksCollection - a collection of keystores indexed by public address. An extra
       *                                property, owner, holds the keystore that signs the outer message
 *                                      and hence becomes the owner of the proxy keys.
 * @returns a Promise which resolves to an IdKeystore or rejects with an instance of Error
 */
exports.requestProxyKey = async function identity$$requestProxyKey(ksCollection, messageDetails)
{
  const endpoint = dcpConfig.pxAuth.location.resolve('new-px');
  const expiry = Math.floor(Date.now()/1000) + 3600;
  const messageId = ksCollection.get('owner').address + '-' + randStr21(); /* random string to tie inner/outer messages together */

  assert(ksCollection instanceof exports.KsCollection);

  /* Transform the collection of keystore for auth addresses (bank accounts) into unlocked keystores,
   * so that we can avoid triggering the unlock dialog multiple times while creating the signed message
   * (that itself contains signed messages!). This has perf impacts that would be resolved by DCP-4423.
   */
  for (let address of ksCollection.getKeys())
  {
    if (address === 'owner')
      continue;
    const ks = ksCollection.get(address);
    debugging() && console.debug(`identity - adding '${address}' to keystore collection for new-px request`);
    ksCollection.add(await ks.getPrivateKey(), address);
  }

  busy();
  for (let i=0; i < messageDetails.pxRequest.length; i++)
  {
    const component = messageDetails.pxRequest[i];

    /* Care must be taken here to never encode values like NaN which JSON can silently transform into
     * seemingly-correct values like null, evading the sanity checking in the back end.
     */
    const params = Object.values(component)[0];
    for (const param in params)
    {
      const value = params[param];
      if (value === null || typeof value === 'string')
        continue;
      if (typeof value !== 'number')
        throw new Error(`invalid parameter ${param}; type is ${typeof value}`);
      if (isNaN(value ))
        throw new Error(`invalid parameter ${param} value is NaN`);
    }

    const authAddress = params.auth;
    if (!authAddress)
      continue;

    if (!ksCollection.has(authAddress))
      throw new Error(`pxRequest[${i}] has incorrect auth address`);

    /* Going through the explanation phase for "auth" parameters adds an important extra semantic to the
     * pxRequest - a human with the authority to do so has granted permission for the proxification of
     * something more than just their identity; e.g their bank account.  This necessarily involves a
     * representation difference pre- and post-explanation. How we represent that difference to new-px
     * is by changing the pxRequest component into a signed message. The message body holds the original
     * pxRequest component, but the address is the "owner" property of the signed message, and we know
     * this authority has been granted because the message has been signed.
     *
     * The outer signed message, which contains pxRequest also has an owner property; this one is the
     * owner of the proxy row; i.e. the identity whose auth context we are working with. The inner
     * signed message(s) have messageId propoerty, which must match the outer message to be valid.
     * This is how we tie the inner and the outer messages together, making it impossible to reuse an
     * inner message for a different owner or in a way the owner didn't explicity approve of.
     */
    const innerMessage = {
      pxRequestComponent: component,
      parentMessageId: messageId,
    };
    messageDetails.pxRequest[i] = await ksCollection.get(authAddress).makeSignedMessage(innerMessage);
  }

  const messageBody = Object.assign({ messageId, expiry, endpoint }, messageDetails);
  const result = await submitSignedPost(ksCollection.get('owner'), messageBody, { busy, unbusy });
  if (result instanceof Error)
    throw result;

  if (!result.keystore)
    throw new Error(`new-px failure ${result.failure} - ${result.message}`);

  return result.keystore;
}

exports.clearSession = function identity$$clear()
{
  identity.emit('clearSession', activeSession);
  sessionStorage.removeItem('dcp-current-session-id');
  localStorage  .removeItem('dcp-current-session-id');
  sessionStorage.removeItem('dcp-current-session-snapshot');
  localStorage  .removeItem('dcp-current-session-snapshot');
  for (let prop in activeSession)
    delete activeSession[prop];
  activeSession = false;
}

/**
 * Recover the Distributive session from sessionStorage or localStorage. This is used during the login
 * process to figure out the session id and maybe the session key during window reload, redirect back
 * from auth host, etc.
 *
 * Recovery can be a bit tricky, because the first time we get the session identity, it is memoized in
 * the origin that authorized it; this means that we need both the snapshot and the memo to get all
 * the information that is known about the session.
 *
 * On subsequent session recoveries, it would be possible to use an identity that was stored in the
 * current origin, however I have elected not to do that; instead, letting the get-session-key become
 * a "choke point" where we can evaluate the session key on a page-load-by-page-load basis. This should
 * make easier to implement session key expiry/invalidation at a later date.
 *
 * @param {any} pxRequest    as passed to login; used to ensure that current session is privileged enough
 *                           for the resume operation
 * @param {Object} options   as passed to login; see pxRequest
 */
exports.recoverSession = async function identity$$recoverSession()
{
  const { DistributiveSession } = require('./distributive-session');

  async function recoverFromStorage(storageHnd)
  {
    const serializedSnapshot = storageHnd.getItem('dcp-current-session-snapshot');
    if (!serializedSnapshot)
      return false;

    try
    {
      const marshaledSnapshot = JSON.parse(serializedSnapshot);
      delete marshaledSnapshot.what.ps.identity; /* workaround for DCP-4472 */
      const snapshot = KVIN.unmarshal(marshaledSnapshot);
      const sessionId = snapshot.id;
      const session = new DistributiveSession(sessionId);
      const memo = await DistributiveSession.a$getSessionMemo(sessionId);
      if (!memo)
        return false;

      session.restoreFromMemo(memo);
      delete snapshot.id;
      delete snapshot.identity;
      Object.assign(session, snapshot);
      exports.activateSession(session);

      return true;
    }
    catch(error) /* necessary to catch storage-format changes as software evolves */
    {
      console.error('error recovering session from storage:', error);
      console.error('removing corrupted session snapshot', serializedSnapshot);
      storageHnd.removeItem('dcp-current-session-snapshot');
      return false;
    }
  }

  return await recoverFromStorage(sessionStorage) || await recoverFromStorage(localStorage);
}

/**
 * Normalize a pxRequest into the array of objects syntax.
 *
 * valid syntaxes that are all equivalent
 * [ 'bank.account.link.job.new', 'job.exec' ]
 *
 * [
 *   { 'bank.account.link.job.new': true },
 *   { 'job.exec': true },
 * ]
 *
 * {
 *   'bank.account.link.job.new': true,
 *   'job.exec': true,
 * },
 *
 * [
 *   { 'bank.account.link.job.new: { amount: null, uses: null, related: null, auth: null },
 *   { 'job.exec': { amount: null, uses: null, related: null, auth: null } },
 * ]
 *
 * Other valid pxRequests include:
 *  - '$login'
 *  - { 'bank.account.transfer.limit': { amount: '1337', auth: '0xba148acc000147' } }; // NYI
 *
 * @param {any}  pxRequest  - a string, an object, an array of strings, or an array of objects
 *                            representing the desured characteristics of the proxy key.
 *
 */
exports.normalizePxRequest = async function normalizePxRequest(pxRequest)
{
  /* normalize the request into Array of Objects */
  if (typeof pxRequest === 'string')
    pxRequest = [ pxRequest ];
  else if (!Array.isArray(pxRequest))
    pxRequest = Object.entries(pxRequest).map(entry => ({ [entry[0]]: entry[1] }));    /* multi-key object into array of objects */
  pxRequest = pxRequest.map(pxr => typeof pxr === 'string' ? ({ [pxr]: true }) : pxr); /* array of strings into array of objects */

  await Promise.all(pxRequest.map(component => exports.normalizePxRequestComponent(component)));
  /* normalizePxRequestComponent mutates pxRequest component innards */
  return pxRequest;
}

/**
 * Normalize one element of a pxRequest array. See normalizePxRequest() details.
 * Works by mutating component.
 */
exports.normalizePxRequestComponent = async function normalizePxRequestComponent(component)
{
  const componentKeys   = Object.keys(component);
  const componentValues = Object.values(component);
  const operation = componentKeys[0];

  assert(typeof component === 'object');
  if (componentKeys.length !== 1)
    throw new Error(`pxRequest component objects must have exactly one key; found ${componentKeys.join(', ')}`);

  let params = componentValues[0];
  if (params === true)
  {
    component[operation] = { uses: null, amount: null, related: null };
    return;
  }

  if (!params.replaceId)
  {
    /* replaceId used by new-px indicates that the px request is a request to mutate. As such, undefined
     * properties are allowed in that context, because undefined means "do not mutate".
     */
    if (!params.hasOwnProperty('uses'))
      params.uses = null;
    if (!params.hasOwnProperty('related'))
      params.related = null;
    if (!params.hasOwnProperty('amount'))
      params.amount = null;
  }
  if (params.amount !== undefined && params.amount !== null && !(params.amount instanceof BigNumber))
    params.amount = new BigNumber(params.amount);
  if (typeof params.auth === 'object' && !(params.auth instanceof Keystore))
    params.auth = await new Keystore(params.auth);

  return component;
}

/* Busy/Unbusy UI hinting functions */
exports.busy = busy;
function busy()
{
  identity.emit('busy');
}

exports.unbusy = unbusy;
function unbusy()
{
  identity.emit('unbusy');
}

/**
 * Make this session the active session
 */
exports.activateSession = function identity$$activateSession(session)
{
  if (activeSession)
  {
    debugging() && console.debug('deactivating session', activeSession.id);
    delete activeSession.identity;
    delete activeSession.id;
  }

  debugging() && console.debug('activating session', session?.id);
  if (session.persist)
    localStorage.setItem('dcp-current-session-id', session.id);
  else
    localStorage.removeItem('dcp-current-session-id', session.id);
  sessionStorage.setItem('dcp-current-session-id', session.id);

  activeSession = session;
  if (activeSession.identity)
  {
    identity.emit('set', activeSession.identity);
    wallet.addId(session.identity); /* backwards compat for older parts of dcp-client using wallet for identity */
    if (activeSession.operationKeys?.find(key => key.startsWith('bank.')))
      wallet.add(session.identity); /* this identity also functions as a proxy for payment */
  }

  identity.emit('session', activeSession);
}

exports.KsCollection = function identity$$KsCollection()
{
  this.__idx = {};
}
exports.KsCollection.prototype.add = function identity$$KsCollection$add(ks, lookupKey)
{
  if (!(ks instanceof Keystore) && !(ks instanceof PrivateKey))
    throw new Error('ks is not an instance of Keystore');

  if (!lookupKey)
    lookupKey = ks.address;
  if (!this.__idx[lookupKey])
    this.__idx[lookupKey] = ks;
}
exports.KsCollection.prototype.get = function identity$$KsCollection$get(lookupKey)
{
  if (!this.__idx.hasOwnProperty(lookupKey))
    throw new Error('KsCollection missing lookupKey ' + lookupKey);
  return this.__idx[lookupKey];
}
exports.KsCollection.prototype.has = function identity$$KsCollection$add(lookupKey)
{
  return this.__idx.hasOwnProperty(lookupKey);
}
exports.KsCollection.prototype.getKeys = function identity$$KsCollection$add()
{
  return Object.keys(this.__idx);
}

Object.assign(exports, require('./distributive-session'));
Object.assign(exports, require('./auth-context'));

if (!isBrowserPlatform)
  exports.login = exports.login_notBrowser;
else
{
  /* Platform-sensitive exports */
  exports.login = exports.login_browser;

  /* Half-hearted attempt to persist DistributiveSession object automatically */
  window.addEventListener('beforeunload', (ev) => {
    if (activeSession?.identity)
      activeSession.save();
  });
}

/** exports.interactive=false forces us to fallback to reading id.keystore from disk */
exports.interactive = (isBrowserPlatform || platform === 'pythonmonkey') || requireNative('process').stdin.isTTY;

/* API: these classes should be thought of as part of the identity subsystem, not the wallet API */
exports.IdKeystore = wallet.IdKeystore;
exports.Address    = wallet.Address;
exports.PrivateKey = wallet.PrivateKey;

/* API: identity.pxa is the official way application programmers should access proxy key names etc */
exports.pxa = require('dcp/pxa');
